import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "@env";
import { dateCompareIfNotNull, sorted, toMap } from "@utils";
import {
  BehaviorSubject,
  filter,
  first,
  firstValueFrom,
  forkJoin,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
  timer,
} from "rxjs";
import {
  CortiDataResponse,
  CreateDocumentResponse,
  CreateInteractionResponse,
  CreateTranscriptResponse,
  Document,
  DocumentSection,
  ExtractFactsResponse,
  Fact,
  FactGroup,
  FactsCreateInput,
  GetDocumentResponse,
  GetDocumentsResponse,
  GetInteractionResponse,
  GetInteractionsResponse,
  GetTemplatesResponse,
  GetTranscriptResponse,
  GetTranscriptsResponse,
  GetTranscriptStatus,
  InteractionListItem,
  ScribeJob,
  ScribeJobStatus,
  StreamConfig,
  StreamIncomingMessage,
  Template,
  TranscriptItem,
  TranscriptStatus,
} from "types/corti";
import { DeepPartial } from "utility-types";
import { ApiService } from "./api.service";
import { AuthService } from "./auth.service";

export enum ScribeRecordingState {
  NeverStarted = "never-started",
  RecordingStarting = "recording-starting",
  Recording = "recording",
  Paused = "paused",
  Processing = "processing",
}

@Injectable({
  providedIn: "root",
})
export class ScribeService {
  private interactionsSubject = new BehaviorSubject<
    InteractionListItem[] | null
  >(null);

  public interactions$ = this.interactionsSubject.asObservable();

  public created = new Subject<CreateInteractionResponse>();

  private templateSubject = new BehaviorSubject<Template[] | null>(null);
  public templates$ = this.templateSubject.asObservable();

  private factGroupsSubject = new BehaviorSubject<FactGroup[] | null>(null);
  public factGroups$ = this.factGroupsSubject.asObservable();

  private recordingStateSubject =
    new BehaviorSubject<ScribeRecordingState | null>(null);

  public recordingState$ = this.recordingStateSubject.asObservable();

  public constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private httpClient: HttpClient
  ) {}

  /**
   * Logs a message with the `[Scribe]` prefix. No-op in production.
   */
  private log(...args: unknown[]): void {
    if (environment.production) return;
    // eslint-disable-next-line no-console
    console.log("[Scribe]", ...args);
  }

  public getRecordingState(): ScribeRecordingState | null {
    return this.recordingStateSubject.value;
  }

  public setRecordingState(state: ScribeRecordingState) {
    this.recordingStateSubject.next(state);
  }

  public mapInteractionToListItem(
    interaction: GetInteractionResponse
  ): InteractionListItem {
    return {
      id: interaction.id,
      encounter: {
        identifier: interaction.encounter.identifier,
        title: interaction.encounter.title,
      },
      patient: interaction.patient,
      endedAt: interaction.endedAt,
      assignedUserId: interaction.assignedUserId,
      createdAt: interaction.createdAt,
      updatedAt: interaction.updatedAt,
      websocketUrl: interaction.websocketUrl,
      lastUpdated: interaction.lastUpdated,
    };
  }

  public mergeInteractions(
    previous: InteractionListItem[],
    current: InteractionListItem[]
  ): InteractionListItem[] {
    const interactionsMap = toMap(
      previous,
      (s) => s.id,
      (s) => s
    );

    for (const interaction of current) {
      interactionsMap.set(interaction.id, interaction);
    }

    const mergedInteractions = Array.from(
      interactionsMap.entries(),
      ([_, value]) => value
    );

    const sortedInteractions = sorted(mergedInteractions, (a, b) =>
      dateCompareIfNotNull(a.lastUpdated, b.lastUpdated)
    );

    return sortedInteractions;
  }

  private updateInteractions(interactions: InteractionListItem[]) {
    const mergedInteractions = this.mergeInteractions(
      this.interactionsSubject.value ?? [],
      interactions
    );
    this.interactionsSubject.next(mergedInteractions);
  }

  public getInteraction(
    interactionId: string
  ): Observable<GetInteractionResponse> {
    return this.apiService
      .get<GetInteractionResponse>({
        path: `/api/scribe/v3/interactions/${interactionId}`,
      })
      .pipe(
        tap((response) => {
          const interaction = this.mapInteractionToListItem(response);
          this.updateInteractions([interaction]);
        })
      );
  }

  public getInteractions(
    paginationToken: string = null,
    pageSize: number = 20
  ): Observable<GetInteractionsResponse> {
    return this.apiService
      .get<GetInteractionsResponse>({
        path: `/api/scribe/v3/interactions/`,
        queryParams: {
          token: paginationToken,
          pageSize: pageSize,
        },
      })
      .pipe(tap((response) => this.updateInteractions(response.data)));
  }

  public createInteraction(): Observable<CreateInteractionResponse> {
    return this.apiService.post<CreateInteractionResponse>({
      path: `/api/scribe/v3/interactions/`,
      body: {
        encounter: {
          identifier: crypto.randomUUID(),
          status: "planned",
          type: "consultation",
          period: {
            startedAt: new Date().toISOString(),
          },
          title: "Untitled Session",
        },
      },
    });
  }

  public optimisticallyUpdateLastUpdatedTime(interactionId: string) {
    const interaction = this.interactionsSubject.value?.find(
      (i) => i.id === interactionId
    );
    if (!interaction) return;
    const now = new Date().toISOString();
    const updatedInteraction: InteractionListItem = {
      ...interaction,
      updatedAt: now,
      lastUpdated: now,
    };
    this.updateInteractions([updatedInteraction]);
  }

  public updateLastUpdatedTime(interactionId: string) {
    // This is a workaround as Corti doesn't allow directly updating the updatedAt value
    return this.patchInteraction(interactionId, {
      encounter: {
        period: {
          endedAt: new Date().toISOString(),
        },
      },
    });
  }

  private updateLastUpdatedTimeTap<T>(interactionId: string) {
    // we could use switchMap here, but that would delay emissions from the outer observable
    return tap<T>({
      next: () => {
        this.updateLastUpdatedTime(interactionId).subscribe();
      },
    });
  }

  public patchInteraction(
    interactionId: string,
    body: DeepPartial<GetInteractionResponse>
  ): Observable<GetInteractionResponse> {
    return this.apiService
      .patch<GetInteractionResponse>({
        path: `/api/scribe/v3/interactions/${interactionId}`,
        body: body,
      })
      .pipe(
        tap((response) => {
          const interaction = this.mapInteractionToListItem(response);
          this.updateInteractions([interaction]);
        })
      );
  }

  public uploadAudio(
    interactionId: string,
    audioBlob: Blob,
    recordingDurationSeconds: number
  ): Observable<{ recordingId: string }> {
    return this.httpClient.post<{ recordingId: string }>(
      `${environment.celoApiEndpoint}/api/scribe/v3/interactions/${interactionId}/recordings/`,
      audioBlob,
      {
        headers: {
          "Content-Type": "application/octet-stream",
          "X-Scribe-Recording-Duration-Seconds": Math.round(
            recordingDurationSeconds
          ).toString(),
        },
      }
    );
  }

  public uploadAndProcessRecording(
    interactionId: string,
    audioBlob: Blob,
    recordingDurationSeconds: number,
    idempotencyKey: string
  ): Observable<ScribeJob> {
    const formData = new FormData();
    formData.append("recording", audioBlob);

    return this.httpClient.post<ScribeJob>(
      `${environment.celoApiEndpoint}/api/scribe/v3/interactions/${interactionId}/recordings/process`,
      formData,
      {
        headers: {
          "X-Scribe-Recording-Duration-Seconds": Math.round(
            recordingDurationSeconds
          ).toString(),
          "X-Idempotency-Key": idempotencyKey,
        },
      }
    );
  }

  public getTranscript(
    interactionId: string,
    transcriptId: string
  ): Observable<GetTranscriptResponse> {
    return this.httpClient.get<GetTranscriptResponse>(
      `${environment.celoApiEndpoint}/api/scribe/v3/interactions/${interactionId}/transcripts/${transcriptId}/`
    );
  }

  public getTranscripts(interactionId: string): Observable<TranscriptItem[]> {
    return this.httpClient
      .get<GetTranscriptResponse>(
        `${environment.celoApiEndpoint}/api/scribe/v3/interactions/${interactionId}/transcripts/`
      )
      .pipe(map((response) => response.transcripts));
  }

  public getTranscriptsWithEntries(
    interactionId: string
  ): Observable<GetTranscriptResponse[]> {
    return this.httpClient
      .get<GetTranscriptsResponse>(
        `${environment.celoApiEndpoint}/api/scribe/v3/interactions/${interactionId}/transcripts/`
      )
      .pipe(
        switchMap((transcripts) => {
          if (!transcripts?.transcripts?.length) return of([]);
          return forkJoin(
            transcripts.transcripts.map((t) => {
              return this.getTranscript(interactionId, t.id);
            })
          );
        })
      );
  }

  public createTranscript(
    interactionId: string,
    recordingId: string,
    primaryLanguage: string = "en"
  ) {
    return this.apiService.post<CreateTranscriptResponse>({
      path: `/api/scribe/v3/interactions/${interactionId}/transcripts/`,
      body: {
        recordingId,
        primaryLanguage,
        diarize: true,
      },
    });
  }

  public getTranscriptStatus(
    interactionId: string,
    transcriptId: string
  ): Observable<GetTranscriptStatus> {
    return this.apiService.get<GetTranscriptStatus>({
      path: `/api/scribe/v3/interactions/${interactionId}/transcripts/${transcriptId}/status`,
    });
  }

  public pollTranscriptStatusUntilStatus(
    interactionId: string,
    transcriptId: string,
    statuses: TranscriptStatus[],
    interval = 2000
  ) {
    return timer(0, interval).pipe(
      switchMap(() => this.getTranscriptStatus(interactionId, transcriptId)),
      filter((response) => statuses.includes(response.status)),
      first()
    );
  }

  public extractFacts(transcripts: TranscriptItem[]) {
    const text = transcripts.map((data) => data.text).join(" ");
    return this.apiService.post<ExtractFactsResponse>({
      path: `/api/scribe/v3/tools/extract-facts/`,
      body: {
        outputLanguage: "en",
        context: [
          {
            type: "text",
            text,
          },
        ],
      },
    });
  }

  public getDocuments(interactionId: string): Observable<Document[]> {
    return this.apiService
      .get<GetDocumentsResponse>({
        path: `/api/scribe/v3/interactions/${interactionId}/documents/`,
      })
      .pipe(map((response) => response.data));
  }

  public getDocument(
    interactionId: string,
    documentId: string
  ): Observable<Document> {
    return this.apiService
      .get<GetDocumentResponse>({
        path: `/api/scribe/v3/interactions/${interactionId}/documents/${documentId}`,
      })
      .pipe(map((response) => response));
  }

  public createDocument(
    interactionId: string,
    facts: Fact[],
    templateKey: string
  ): Observable<CreateDocumentResponse> {
    return this.apiService
      .post<CreateDocumentResponse>({
        path: `/api/scribe/v3/interactions/${interactionId}/documents/`,
        body: {
          context: [
            {
              type: "facts",
              data: facts.map((fact) => ({
                text: fact.text,
                group: fact.group,
                source: "core",
              })),
            },
          ],
          templateKey,
          outputLanguage: "en",
        },
      })
      .pipe(this.updateLastUpdatedTimeTap(interactionId));
  }

  public patchDocument(
    interactionId: string,
    documentId: string,
    section: DocumentSection
  ): Observable<Document> {
    this.optimisticallyUpdateLastUpdatedTime(interactionId);
    return this.apiService
      .patch<GetDocumentResponse>({
        path: `/api/scribe/v3/interactions/${interactionId}/documents/${documentId}`,
        body: {
          sections: [section],
        },
      })
      .pipe(this.updateLastUpdatedTimeTap(interactionId));
  }

  public getTemplates(): Observable<Template[]> {
    return this.apiService
      .get<GetTemplatesResponse>({
        path: "/api/scribe/v3/templates",
      })
      .pipe(
        map((response) => response.data),
        tap((templates) => {
          this.templateSubject.next(templates);
        }),
        switchMap(() => this.templates$)
      );
  }

  public getFactGroups() {
    return this.apiService
      .get<CortiDataResponse<FactGroup[]>>({
        path: `/api/scribe/v3/factgroups`,
      })
      .pipe(
        map((response) => response.data),
        tap((groups) => {
          this.factGroupsSubject.next(groups);
        })
      );
  }

  public getFacts(interactionId: string): Observable<Fact[]> {
    return this.apiService
      .get<{ facts: Fact[] }>({
        path: `/api/scribe/v3/interactions/${interactionId}/facts`,
      })
      .pipe(map((response) => response.facts));
  }

  public addFacts(
    interactionId: string,
    facts: FactsCreateInput[]
  ): Observable<Fact[]> {
    this.optimisticallyUpdateLastUpdatedTime(interactionId);
    return this.apiService
      .post<{ facts: Fact[] }>({
        path: `/api/scribe/v3/interactions/${interactionId}/facts`,
        body: { facts },
      })
      .pipe(
        map((response) => {
          //This is added because the backend returns the wrong createdAt value
          const createdAtAddedFacts = response.facts.map((fact) => ({
            ...fact,
            createdAt: new Date().toISOString(),
          }));

          return createdAtAddedFacts;
        })
      )
      .pipe(this.updateLastUpdatedTimeTap(interactionId));
  }

  public addFact(
    interactionId: string,
    fact: FactsCreateInput
  ): Observable<Fact[]> {
    return this.addFacts(interactionId, [fact]);
  }

  public updateFact(
    interactionId: string,
    factId: string,
    updateFact: {
      text?: string;
      group?: string;
      source?: string;
      isDiscarded?: boolean;
    }
  ): Observable<Fact> {
    this.optimisticallyUpdateLastUpdatedTime(interactionId);
    return this.apiService
      .patch<Fact>({
        path: `/api/scribe/v3/interactions/${interactionId}/facts/${factId}`,
        body: updateFact,
      })
      .pipe(
        this.updateLastUpdatedTimeTap(interactionId),
        map((response) => response)
      );
  }

  public getJob(
    interactionId: string,
    jobId: string
  ): Observable<ScribeJob> {
    return this.apiService.get<ScribeJob>({
      path: `/api/scribe/v3/interactions/${interactionId}/jobs/${jobId}`,
    });
  }

  public pollJobUntilStatus(
    interactionId: string,
    jobId: string,
    statuses: ScribeJobStatus[],
    interval = 2000,
    cancel$?: Observable<void>
  ) {
    let poll$ = timer(0, interval).pipe(
      switchMap(() => this.getJob(interactionId, jobId)),
      filter((response) => statuses.includes(response.status)),
      first()
    );

    if (cancel$) {
      poll$ = poll$.pipe(takeUntil(cancel$));
    }

    return poll$;
  }

  public requestAccess(): Observable<null> {
    return this.apiService
      .post<string>({
        path: `/api/v3/user/registry/scribeAccessRequest`,
      })
      .pipe(
        map((_) => {
          return null;
        })
      );
  }

  public getStreamToken(interactionId: string): Observable<string> {
    return this.apiService
      .post<{ token: string }>({
        path: `/api/scribe/v3/interactions/${interactionId}/stream-token`,
      })
      .pipe(map((response) => response.token));
  }

  /**
   * Opens a WebSocket connection to the streaming proxy endpoint for real-time
   * transcription and fact extraction.
   *
   * Uses the proxy at /api/scribe/v3/interactions/{id}/streams which handles
   * tenant and token authentication automatically.
   *
   * Returns a Promise that resolves with the connected WebSocket after
   * CONFIG_ACCEPTED is received, or rejects on config denial/timeout.
   */
  public async connectStream(
    interactionId: string,
    configuration: StreamConfig,
    signal?: AbortSignal
  ): Promise<WebSocket> {
    const token = await firstValueFrom(this.getStreamToken(interactionId));

    if (signal?.aborted) {
      throw new Error("ABORTED");
    }

    const httpUrl = `${environment.celoApiEndpoint}/api/scribe/v3/interactions/${interactionId}/stream`;
    const queryParams = new URLSearchParams({ accessToken: token });
    const drid = this.authService.getDataRegion();
    if (drid) queryParams.set("drid", drid);
    const wsUrl = httpUrl.replace(/^http/, "ws") + `?${queryParams.toString()}`;

    return new Promise<WebSocket>((resolve, reject) => {
      if (signal?.aborted) {
        reject(new Error("ABORTED"));
        return;
      }

      this.log(`Connecting to stream WebSocket: ${wsUrl}`);
      const ws = new WebSocket(wsUrl);

      const configTimeout = setTimeout(() => {
        this.log("Stream WebSocket timed out waiting for CONFIG_ACCEPTED");
        ws.close();
        reject(new Error("CONFIG_TIMEOUT: Timed out waiting for config acceptance"));
      }, 10000);

      signal?.addEventListener("abort", () => {
        clearTimeout(configTimeout);
        ws.close();
        reject(new Error("ABORTED"));
      });

      ws.onopen = () => {
        this.log("Stream WebSocket connected, sending config");
        const configMessage = JSON.stringify({
          type: "config",
          configuration,
        });
        ws.send(configMessage);
      };

      ws.onmessage = (event) => {
        let msg: StreamIncomingMessage;
        try {
          msg = JSON.parse(event.data);
        } catch {
          this.log("Stream WebSocket received non-JSON message:", event.data);
          return;
        }

        if (msg.type === "CONFIG_ACCEPTED") {
          clearTimeout(configTimeout);
          this.log("Stream WebSocket config accepted");
          // Remove this temporary handler; caller will set their own
          ws.onmessage = null;
          resolve(ws);
        } else if (
          msg.type === "CONFIG_DENIED" ||
          msg.type === "CONFIG_NOT_PROVIDED" ||
          msg.type === "CONFIG_ALREADY_RECEIVED" ||
          msg.type === "CONFIG_MISSING"
        ) {
          clearTimeout(configTimeout);
          const reason = (msg as any).reason ?? "Configuration rejected";
          this.log(`Stream WebSocket config rejected: ${msg.type} - ${reason}`, msg);
          ws.close();
          reject(new Error(`${msg.type}: ${reason}`));
        }
      };

      ws.onerror = (event) => {
        clearTimeout(configTimeout);
        this.log("Stream WebSocket error:", event);
        reject(new Error("WebSocket connection error"));
      };

      ws.onclose = (event) => {
        clearTimeout(configTimeout);
        this.log(`Stream WebSocket closed unexpectedly: code=${event.code}, reason=${event.reason}, wasClean=${event.wasClean}`);
        reject(new Error(`WebSocket closed before config accepted: ${event.code} ${event.reason}`));
      };
    });
  }
}
